import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { fetchWorkspaces, Workspace } from '../lib/api/workspaces';

const STORAGE_KEY = 'selected_workspace_id';

interface WorkspaceContextValue {
  currentWorkspaceId: string | null;
  currentWorkspace: Workspace | null;
  workspaces: Workspace[];
  loading: boolean;
  isFiltered: boolean;
  setWorkspace: (id: string | null) => void;
  refreshWorkspaces: () => Promise<void>;
}

const WorkspaceContext = createContext<WorkspaceContextValue | undefined>(undefined);

export function WorkspaceProvider({ children }: { children: React.ReactNode }) {
  const [workspaces, setWorkspaces] = useState<Workspace[]>([]);
  const [loading, setLoading] = useState(true);
  const [currentWorkspaceId, setCurrentWorkspaceId] = useState<string | null>(
    localStorage.getItem(STORAGE_KEY)
  );

  const loadWorkspaces = useCallback(async () => {
    try {
      setLoading(true);
      const response = await fetchWorkspaces({ is_active: true });
      const list = response.results;
      setWorkspaces(list);

      // Validate stored ID against fetched list
      const storedId = localStorage.getItem(STORAGE_KEY);
      if (storedId && !list.some((ws) => String(ws.id) === storedId)) {
        // Stored workspace was deleted or deactivated — reset
        localStorage.removeItem(STORAGE_KEY);
        setCurrentWorkspaceId(null);
      }

      // Auto-select if exactly one workspace
      if (list.length === 1 && !storedId) {
        localStorage.setItem(STORAGE_KEY, String(list[0].id));
        setCurrentWorkspaceId(String(list[0].id));
      }
    } catch (err) {
      console.error('Failed to fetch workspaces:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadWorkspaces();
  }, [loadWorkspaces]);

  const setWorkspace = useCallback((id: string | null) => {
    if (id) {
      localStorage.setItem(STORAGE_KEY, String(id));
    } else {
      localStorage.removeItem(STORAGE_KEY);
    }
    setCurrentWorkspaceId(id);
  }, []);

  const currentWorkspace = workspaces.find((ws) => ws.id === currentWorkspaceId) ?? null;

  return (
    <WorkspaceContext.Provider
      value={{
        currentWorkspaceId,
        currentWorkspace,
        workspaces,
        loading,
        isFiltered: currentWorkspaceId !== null,
        setWorkspace,
        refreshWorkspaces: loadWorkspaces,
      }}
    >
      {children}
    </WorkspaceContext.Provider>
  );
}

export function useWorkspace(): WorkspaceContextValue {
  const ctx = useContext(WorkspaceContext);
  if (!ctx) {
    throw new Error('useWorkspace must be used within a WorkspaceProvider');
  }
  return ctx;
}
